import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import type { DataProvider, LiveWatch } from "../data";
import type { MessageKey } from "../i18n";
import { errorCode, errorKey } from "../lib/errors";

/**
 * Running live watches (ADR 0014), shared by every screen of one provider so the live map card and
 * the vehicle page show the same state. Seeded from the server (active_live_watches) on mount; while a
 * watch waits for the device's ack it is re-read every few seconds.
 */
export const LIVE_WATCH_POLL_MS = 2_000;

type Entry = { untilMs: number; acked: boolean };
const stores = new WeakMap<DataProvider, { watches: Map<string, Entry>; listeners: Set<() => void> }>();

function storeOf(p: DataProvider) {
  let s = stores.get(p);
  if (!s) stores.set(p, (s = { watches: new Map(), listeners: new Set() }));
  return s;
}

function setWatch(p: DataProvider, w: LiveWatch): void {
  const s = storeOf(p);
  const untilMs = Date.parse(w.until);
  const cur = s.watches.get(w.vehicleId);
  // A newer request (later end) starts unacknowledged; the same request only moves to acknowledged.
  const next: Entry = !cur || untilMs > cur.untilMs ? { untilMs, acked: w.acked } : { untilMs: cur.untilMs, acked: cur.acked || (w.acked && untilMs === cur.untilMs) };
  if (cur && cur.untilMs === next.untilMs && cur.acked === next.acked) return;
  s.watches.set(w.vehicleId, next);
  for (const fn of s.listeners) fn();
}

export type LiveWatchState = {
  /** Seconds left, 0 when no watch is running. */
  remainingS: number;
  /** Sent, but the device has not confirmed yet. */
  waiting: boolean;
  busy: boolean;
  error: MessageKey | null;
  start: () => Promise<void>;
};

const ERROR_TEXT: Partial<Record<string, MessageKey>> = {
  // The generic rate-limit text talks about invitations; say what applies here.
  rate_limited: "liveWatch.tooSoon",
};

export function useLiveWatch(provider: DataProvider, tenantId: string, vehicleId: string): LiveWatchState {
  const store = storeOf(provider);
  const subscribe = useCallback(
    (fn: () => void) => {
      store.listeners.add(fn);
      return () => void store.listeners.delete(fn);
    },
    [store],
  );
  const entry = useSyncExternalStore(subscribe, () => store.watches.get(vehicleId) ?? null);
  const [now, setNow] = useState(() => Date.now());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<MessageKey | null>(null);

  const refresh = useCallback(async () => {
    const list = await provider.listLiveWatches(tenantId);
    for (const w of list) setWatch(provider, w);
  }, [provider, tenantId]);

  useEffect(() => {
    let cancelled = false;
    refresh().catch(() => undefined); // the state display is a convenience
    return () => {
      cancelled = true;
      void cancelled;
    };
  }, [refresh]);

  const active = entry !== null && entry.untilMs > now;
  const waiting = active && !entry.acked;

  // Countdown tick.
  useEffect(() => {
    if (entry === null) return;
    setNow(Date.now());
    if (entry.untilMs <= Date.now()) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [entry, active]);

  // Wait for the device's ack.
  useEffect(() => {
    if (!waiting) return;
    const id = setInterval(() => void refresh().catch(() => undefined), LIVE_WATCH_POLL_MS);
    return () => clearInterval(id);
  }, [waiting, refresh]);

  const start = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setWatch(provider, await provider.startLiveWatch(vehicleId));
      setNow(Date.now());
    } catch (e) {
      setError(ERROR_TEXT[errorCode(e)] ?? errorKey(e));
    } finally {
      setBusy(false);
    }
  }, [provider, vehicleId]);

  return { remainingS: active ? Math.ceil((entry.untilMs - now) / 1000) : 0, waiting, busy, error, start };
}

/** 9:41 style countdown. */
export function formatCountdown(s: number): string {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
